import type { IAffiliationsSchema } from "../types/models/Affiliations.model";
import type { IAuthorSchema } from "../types/models/Author.model";
import type { IJournalSchema } from "../types/models/Journal.model";
import type { IResearchTopicSchema } from "../types/models/ResearchTopic.model";
import type { IAffiliation, IAuthor, IResearchTopic } from "../types";
import { JournalSchema2Interface } from "./JournalSchema2Interface";

export const ResearchTopicSchema2Interface = (
  researchTopicSchema: IResearchTopicSchema,
  rtJournalDetails: IJournalSchema[],
  ebooks: string[],
  tenantName: string
): IResearchTopic => {
  return {
    abstractSubmissionDeadline: researchTopicSchema.AbstractSubmissionDeadline,
    authorsCount: researchTopicSchema.AuthorsCount,
    onlineSince: researchTopicSchema.OnlineSince
      ? String(researchTopicSchema.OnlineSince)
      : undefined,
    closedOn: researchTopicSchema.ClosedOn
      ? String(researchTopicSchema.ClosedOn)
      : undefined,
    createdDate: researchTopicSchema.CreatedDate,
    description: researchTopicSchema.Description,
    id: researchTopicSchema.ResearchTopicId,
    impactV5: researchTopicSchema.ImpactV5 && {
      views: transformNumberToSafeInteger(researchTopicSchema.ImpactV5.Views),
      downloads: transformNumberToSafeInteger(
        researchTopicSchema.ImpactV5.Downloads
      ),
      articleViews: transformNumberToSafeInteger(
        researchTopicSchema.ImpactV5.ArticleViews
      )
    },
    imageUrl: researchTopicSchema.ImageUrl,
    availableEbooks: ebooks,
    isOpenForManuscriptSubmission:
      researchTopicSchema.IsOpenForManuscriptSubmission,
    isOpenForSubmission: researchTopicSchema.IsOpenForSubmission,
    keywords: researchTopicSchema.Keywords
      ? researchTopicSchema.Keywords.map((keyword) => {
          return {
            keywordId: keyword.KeywordId,
            title: keyword.Title
          };
        })
      : [],
    journals: researchTopicSchema.Journals?.map((journal) => {
      const journalDetailIndex = rtJournalDetails.findIndex(
        (journalDetail) => journalDetail.JournalId === journal.JournalId
      );
      const journalDetail = rtJournalDetails[journalDetailIndex];

      return JournalSchema2Interface(journalDetail, journal, tenantName);
    }),
    manuscriptSubmissionExtendedDeadline:
      researchTopicSchema.ManuscriptSubmissionExtendedDeadline,
    manuscriptSubmissionDeadline:
      researchTopicSchema.ManuscriptSubmissionDeadline,
    slug: researchTopicSchema.Slug,
    stageId: researchTopicSchema.StageId,
    title: researchTopicSchema.Title,
    topicCoordinators: getTopicEditorsAndCoordinators(
      researchTopicSchema.TopicCoordinators
    ),
    topicEditors: getTopicEditorsAndCoordinators(
      researchTopicSchema.TopicEditors
    ),
    coverImageUrl: researchTopicSchema.CoverImageUrl
  };
};

function transformNumberToSafeInteger(val: number | undefined): number {
  if (!val) return 0;
  if (Object.keys(val).includes("high")) return Number.MAX_SAFE_INTEGER;
  else return val;
}

function getTopicEditorsAndCoordinators(rtAuthors: IAuthorSchema[]): IAuthor[] {
  const topicAuthors: IAuthor[] = rtAuthors
    ? rtAuthors.map((editor: IAuthorSchema) => {
        return {
          firstName: editor.FirstName,
          email: editor.Email,
          fullName: editor.FullName,
          userId: editor.UserId,
          affiliations: editor.Affiliations
            ? editor.Affiliations.map((aff: IAffiliationsSchema) => {
                return {
                  addressLine1: aff.AddressLine1,
                  addressLine2: aff.AddressLine2,
                  addressLine3: aff.AddressLine3,
                  country: aff.Country,
                  organization: aff.Organization,
                  postalCode: aff.PostalCode,
                  state: aff.State,
                  city: aff.City,
                  department: aff.Department
                } as IAffiliation;
              })
            : [],
          lastName: editor.LastName,
          middleName: editor.MiddleName,
          shortName: editor.ShortName
        } as IAuthor;
      })
    : [];
  return topicAuthors;
}
